import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { motion } from "motion/react";
import {
  CalendarCheck,
  Mail,
  Phone,
  ShieldCheck,
  Stethoscope,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { z } from "zod";
import { toast } from "sonner";
import { useI18n } from "@/lib/i18n";
import { SITE_ORIGIN, breadcrumbSchema, localizedHead, pageMeta, validateLangSearch } from "@/lib/seo";
import { HeroImage } from "@/components/hero-image";
import { consultationImage } from "@/lib/responsive-images";
import { ReviewCta } from "@/components/review-cta";


const LOG_STORAGE_KEY = "retatrutid_contact_log";
const MAX_LOG_ENTRIES = 50;

interface ContactLogEntry {
  id: string;
  timestamp: string;
  name: string;
  email: string;
  phone?: string;
  treatment: string;
  contactMethod: string;
  message?: string;
  lang: string;
}

function generateReference() {
  const now = new Date();
  const y = now.getFullYear().toString().slice(-2);
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `RT-${y}${m}${d}-${rand}`;
}

function logContactRequest(entry: ContactLogEntry) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(LOG_STORAGE_KEY);
    const list: ContactLogEntry[] = raw ? JSON.parse(raw) : [];
    list.unshift(entry);
    window.localStorage.setItem(
      LOG_STORAGE_KEY,
      JSON.stringify(list.slice(0, MAX_LOG_ENTRIES)),
    );
  } catch {
    // storage may be unavailable (private mode); silently ignore
  }
}

// TODO: Ersetzen mit der offiziellen Kontakt-E-Mail-Adresse.
const CONTACT_EMAIL = "beratung@retatrutid.info";

export const Route = createFileRoute("/kontakt")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => {
    const lang = loaderData?.lang;
    const effective = lang ?? "en";
    const copy = pageMeta["/kontakt"][effective];
    const url = `${SITE_ORIGIN}/kontakt${lang ? `?lang=${lang}` : ""}`;
    const contactPage = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": url,
      url,
      name: copy.title,
      description: copy.description,
      inLanguage: effective === "de" ? "de-DE" : "en-US",
      isPartOf: { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website` },
      mainEntity: {
        "@type": "Organization",
        name: "Retatrutid Thailand",
        url: SITE_ORIGIN,
        email: CONTACT_EMAIL,
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: CONTACT_EMAIL,
            areaServed: ["TH", "DE", "AT", "CH"],
            availableLanguage: ["de", "en"],
          },
        ],
      },
    };
    return {
      ...localizedHead("/kontakt", lang),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/kontakt", lang)) },
        { type: "application/ld+json", children: JSON.stringify(contactPage) },
      ],
    };
  },
  component: KontaktPage,
});

// --- Validation ------------------------------------------------------------

const treatmentEnum = z.enum(["retatrutide", "wegovy", "mounjaro", "unsure"]);
const contactMethodEnum = z.enum(["email", "phone"]);

const contactSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Name is too short" })
    .max(100, { message: "Name must be under 100 characters" }),
  email: z
    .string()
    .trim()
    .email({ message: "Please enter a valid email" })
    .max(255, { message: "Email must be under 255 characters" }),
  phone: z
    .string()
    .trim()
    .max(40, { message: "Phone must be under 40 characters" })
    .optional()
    .or(z.literal("")),
  treatment: treatmentEnum,
  contactMethod: contactMethodEnum,
  message: z
    .string()
    .trim()
    .max(1000, { message: "Message must be under 1000 characters" })
    .optional()
    .or(z.literal("")),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please confirm the privacy notice" }),
  }),
});

type ContactValues = z.infer<typeof contactSchema>;

// --- Component -------------------------------------------------------------

function KontaktPage() {
  const { lang } = useI18n();
  const de = lang === "de";
  const t = de ? de_copy : en_copy;

  const [errors, setErrors] = useState<Partial<Record<keyof ContactValues, string>>>({});
  const [sent, setSent] = useState<null | {
    reference: string;
    timestamp: string;
    name: string;
    email: string;
    phone?: string;
    treatmentLabel: string;
    contactLabel: string;
    message?: string;
    mailtoHref: string;
  }>(null);
  const [treatment, setTreatment] = useState<ContactValues["treatment"]>("retatrutide");
  const [contactMethod, setContactMethod] = useState<ContactValues["contactMethod"]>("email");

  const treatmentOptions = t.treatmentOptions;

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const raw = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      treatment,
      contactMethod,
      message: String(fd.get("message") ?? ""),
      consent: fd.get("consent") === "on",
    };
    const parsed = contactSchema.safeParse(raw);
    if (!parsed.success) {
      const fieldErrors: Partial<Record<keyof ContactValues, string>> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0] as keyof ContactValues;
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error(de ? "Bitte prüfe die markierten Felder." : "Please review the highlighted fields.");
      return;
    }
    setErrors({});
    const v = parsed.data;

    const subjectLabel = de ? "Beratungsanfrage" : "Consultation request";
    const treatmentLabel =
      treatmentOptions.find((o) => o.value === v.treatment)?.label ?? v.treatment;
    const contactLabel = v.contactMethod === "phone" ? t.methodPhone : t.methodEmail;
    const reference = generateReference();
    const timestamp = new Date().toISOString();
    const subject = `[Retatrutid Thailand] ${subjectLabel} – ${v.name} (${reference})`;
    const lines = [
      `${de ? "Referenz" : "Reference"}: ${reference}`,
      `${t.fName}: ${v.name}`,
      `${t.fEmail}: ${v.email}`,
      v.phone ? `${t.fPhone}: ${v.phone}` : null,
      `${t.fTreatment}: ${treatmentLabel}`,
      `${t.fMethod}: ${contactLabel}`,
      "",
      `${t.fMessage}:`,
      v.message || "—",
    ].filter(Boolean) as string[];
    const body = lines.join("\n");
    const mailtoHref = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;

    logContactRequest({
      id: reference,
      timestamp,
      name: v.name,
      email: v.email,
      phone: v.phone || undefined,
      treatment: treatmentLabel,
      contactMethod: contactLabel,
      message: v.message || undefined,
      lang,
    });

    setSent({
      reference,
      timestamp,
      name: v.name,
      email: v.email,
      phone: v.phone || undefined,
      treatmentLabel,
      contactLabel,
      message: v.message || undefined,
      mailtoHref,
    });

    toast.success(
      de ? "Anfrage erfasst" : "Request received",
      {
        description: de
          ? `Referenz ${reference} – wir melden uns innerhalb eines Werktags.`
          : `Reference ${reference} – we will reply within one business day.`,
      },
    );

    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 pb-24 pt-12 md:pt-16">
      <motion.header
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <p className="inline-flex items-center gap-2 rounded-full border border-indigo/30 bg-indigo/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-indigo">
          <CalendarCheck aria-hidden="true" className="h-3.5 w-3.5" />
          {t.eyebrow}
        </p>
        <h1 className="mt-4 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {t.h1}
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          {t.lead}
        </p>
      </motion.header>

      <HeroImage
        {...consultationImage}
        alt="Calming premium consultation desk with warm lamp light"
        eager
      />


      <div className="mt-10 grid gap-8 md:grid-cols-[1.4fr_1fr]">
        <section
          aria-labelledby="form-title"
          className="rounded-2xl border border-border bg-card p-6 md:p-8"
        >
          <h2 id="form-title" className="font-display text-2xl text-foreground">
            {t.formTitle}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">{t.formSub}</p>

          {sent ? (
            <div
              role="status"
              aria-live="polite"
              className="mt-6 rounded-xl border border-emerald/40 bg-emerald/10 p-5 text-sm text-foreground"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 aria-hidden="true" className="mt-0.5 h-6 w-6 flex-none text-emerald" />
                <div className="flex-1">
                  <p className="font-display text-lg font-semibold text-foreground">
                    {t.thanksTitle}
                  </p>
                  <p className="mt-1 text-muted-foreground">{t.thanksBody}</p>

                  <dl className="mt-4 grid gap-2 rounded-lg border border-border bg-background/60 p-4 text-xs sm:grid-cols-2">
                    <div>
                      <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                        {de ? "Referenz" : "Reference"}
                      </dt>
                      <dd className="mt-0.5 font-mono text-sm text-foreground">{sent.reference}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                        {de ? "Eingegangen" : "Received"}
                      </dt>
                      <dd className="mt-0.5 text-sm text-foreground">
                        {new Date(sent.timestamp).toLocaleString(de ? "de-DE" : "en-GB")}
                      </dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                        {t.fName}
                      </dt>
                      <dd className="mt-0.5 text-sm text-foreground">{sent.name}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                        {t.fEmail}
                      </dt>
                      <dd className="mt-0.5 truncate text-sm text-foreground">{sent.email}</dd>
                    </div>
                    {sent.phone && (
                      <div>
                        <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                          {t.fPhone}
                        </dt>
                        <dd className="mt-0.5 text-sm text-foreground">{sent.phone}</dd>
                      </div>
                    )}
                    <div>
                      <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                        {t.fTreatment}
                      </dt>
                      <dd className="mt-0.5 text-sm text-foreground">{sent.treatmentLabel}</dd>
                    </div>
                    <div>
                      <dt className="font-semibold uppercase tracking-wider text-muted-foreground">
                        {t.fMethod}
                      </dt>
                      <dd className="mt-0.5 text-sm text-foreground">{sent.contactLabel}</dd>
                    </div>
                  </dl>

                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <a
                      href={sent.mailtoHref}
                      className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-4 py-2 text-xs font-semibold text-background transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      <Mail aria-hidden="true" className="h-3.5 w-3.5" />
                      {de ? "E-Mail-Programm öffnen" : "Open email client"}
                    </a>
                    <button
                      type="button"
                      onClick={() => setSent(null)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {t.thanksAgain}
                    </button>
                  </div>

                  <p className="mt-3 text-[11px] text-muted-foreground">
                    {de
                      ? "Bitte notiere die Referenznummer für Rückfragen."
                      : "Please keep the reference number for follow-up."}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <form noValidate onSubmit={handleSubmit} className="mt-6 grid gap-4">
              <Field
                id="name"
                name="name"
                label={t.fName}
                required
                autoComplete="name"
                maxLength={100}
                error={errors.name}
              />
              <div className="grid gap-4 md:grid-cols-2">
                <Field
                  id="email"
                  name="email"
                  type="email"
                  label={t.fEmail}
                  required
                  autoComplete="email"
                  maxLength={255}
                  error={errors.email}
                />
                <Field
                  id="phone"
                  name="phone"
                  type="tel"
                  label={t.fPhone}
                  autoComplete="tel"
                  maxLength={40}
                  error={errors.phone}
                />
              </div>

              <div>
                <label
                  htmlFor="treatment"
                  className="block text-xs font-semibold uppercase tracking-wider text-foreground"
                >
                  {t.fTreatment}
                </label>
                <select
                  id="treatment"
                  name="treatment"
                  value={treatment}
                  onChange={(e) =>
                    setTreatment(e.target.value as ContactValues["treatment"])
                  }
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                >
                  {treatmentOptions.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>

              <fieldset>
                <legend className="text-xs font-semibold uppercase tracking-wider text-foreground">
                  {t.fMethod}
                </legend>
                <div className="mt-2 flex flex-wrap gap-2">
                  {(
                    [
                      { key: "email", label: t.methodEmail, Icon: Mail },
                      { key: "phone", label: t.methodPhone, Icon: Phone },
                    ] as const
                  ).map(({ key, label, Icon }) => {
                    const active = contactMethod === key;
                    return (
                      <button
                        type="button"
                        key={key}
                        onClick={() => setContactMethod(key)}
                        aria-pressed={active}
                        className={
                          "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background " +
                          (active
                            ? "border-indigo bg-indigo text-indigo-foreground"
                            : "border-border bg-background text-foreground hover:border-indigo/60 hover:text-indigo")
                        }
                      >
                        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
                        {label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div>
                <label
                  htmlFor="message"
                  className="block text-xs font-semibold uppercase tracking-wider text-foreground"
                >
                  {t.fMessage}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  maxLength={1000}
                  placeholder={t.messagePlaceholder}
                  aria-invalid={errors.message ? true : undefined}
                  aria-describedby={errors.message ? "message-error" : undefined}
                  className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                />
                {errors.message && (
                  <FieldError id="message-error" message={errors.message} />
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  id="consent"
                  name="consent"
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-border text-indigo focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-invalid={errors.consent ? true : undefined}
                  aria-describedby={errors.consent ? "consent-error" : undefined}
                />
                <label htmlFor="consent" className="text-xs text-muted-foreground">
                  {t.consent}
                </label>
              </div>
              {errors.consent && <FieldError id="consent-error" message={errors.consent} />}

              <button
                type="submit"
                className="group mt-2 inline-flex items-center justify-center gap-2 self-start rounded-full bg-foreground px-6 py-3 text-sm font-semibold text-background transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {t.submit}
                <ArrowRight
                  aria-hidden="true"
                  className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                />
              </button>
              <p className="text-[11px] italic text-muted-foreground">{t.disclaimer}</p>
            </form>
          )}
        </section>

        <aside aria-labelledby="contact-info" className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h2
              id="contact-info"
              className="text-[11px] font-semibold uppercase tracking-widest text-indigo"
            >
              {t.infoTitle}
            </h2>
            <ul className="mt-3 space-y-3 text-sm text-foreground">
              <li className="flex items-start gap-3">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-md bg-indigo/10 text-indigo">
                  <Mail aria-hidden="true" className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold">{t.infoMail}</p>
                  <a
                    href={`mailto:${CONTACT_EMAIL}`}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    {CONTACT_EMAIL}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-md bg-emerald/10 text-emerald">
                  <Stethoscope aria-hidden="true" className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold">{t.infoTeam}</p>
                  <p className="text-sm text-muted-foreground">{t.infoTeamBody}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="grid h-8 w-8 flex-none place-items-center rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400">
                  <ShieldCheck aria-hidden="true" className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-semibold">{t.infoPrivacy}</p>
                  <p className="text-sm text-muted-foreground">{t.infoPrivacyBody}</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl border border-border bg-card p-5 text-sm">
            <h3 className="font-semibold text-foreground">{t.fyiTitle}</h3>
            <p className="mt-2 text-muted-foreground">{t.fyiBody}</p>
            <div className="mt-3 flex flex-wrap gap-3 text-sm">
              <Link
                to="/vergleich"
                className="text-indigo underline-offset-4 hover:underline"
              >
                {de ? "Vergleich ansehen" : "See comparison"}
              </Link>
              <Link
                to="/sicherheit"
                className="text-indigo underline-offset-4 hover:underline"
              >
                {de ? "Sicherheit & Risiken" : "Safety & risks"}
              </Link>
            </div>
          </div>
        </aside>
      </div>

      <ReviewCta />
    </div>
  );
}


// --- Small UI helpers ------------------------------------------------------

interface FieldProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "id" | "name"> {
  id: string;
  name: string;
  label: string;
  error?: string;
}

function Field({ id, name, label, error, ...rest }: FieldProps) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-xs font-semibold uppercase tracking-wider text-foreground"
      >
        {label}
        {rest.required && <span aria-hidden="true" className="ml-1 text-indigo">*</span>}
      </label>
      <input
        id={id}
        name={name}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className="mt-2 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        {...rest}
      />
      {error && <FieldError id={`${id}-error`} message={error} />}
    </div>
  );
}

function FieldError({ id, message }: { id: string; message: string }) {
  return (
    <p
      id={id}
      role="alert"
      className="mt-1 flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400"
    >
      <AlertCircle aria-hidden="true" className="h-3.5 w-3.5" />
      {message}
    </p>
  );
}

// --- Copy ------------------------------------------------------------------

const en_copy = {
  eyebrow: "Treatment available",
  h1: "Request a personal consultation",
  lead:
    "We offer supervised access to Retatrutide, Wegovy and Mounjaro through a specialised medical team. Tell us a bit about you and we'll get back to you within one working day.",
  formTitle: "Consultation request",
  formSub: "Free initial consultation. No obligation.",
  fName: "Full name",
  fEmail: "Email",
  fPhone: "Phone (optional)",
  fTreatment: "Treatment of interest",
  fMethod: "Preferred contact",
  fMessage: "Your message",
  messagePlaceholder:
    "Anything we should know upfront? Weight goals, existing conditions, timing …",
  methodEmail: "Email",
  methodPhone: "Phone / WhatsApp",
  consent:
    "I have read the privacy notice and agree that my data may be used to answer this request.",
  submit: "Send consultation request",
  disclaimer:
    "This form does not create a doctor–patient relationship. Prescription decisions belong in a clinician's hands.",
  thanksTitle: "Thank you – your request was received.",
  thanksBody:
    "Our medical team will get back to you within one business day. A copy of your request is summarised below for your records.",
  thanksAgain: "Send another request",
  infoTitle: "Direct contact",
  infoMail: "Email us",
  infoTeam: "Medical team",
  infoTeamBody:
    "Consultations are handled by physicians with obesity-medicine and GLP-1 experience.",
  infoPrivacy: "Data & privacy",
  infoPrivacyBody:
    "Your data is used only to answer this request. No newsletter, no third-party sharing.",
  fyiTitle: "Before you write",
  fyiBody:
    "Retatrutide is available through our partner clinic's approved Phase III clinical study program for eligible participants. Wegovy and Mounjaro are approved and can be prescribed today.",
  treatmentOptions: [
    { value: "retatrutide" as const, label: "Retatrutide (clinical study)" },
    { value: "wegovy" as const, label: "Wegovy (Semaglutide)" },
    { value: "mounjaro" as const, label: "Mounjaro (Tirzepatide)" },
    { value: "unsure" as const, label: "Not sure yet – please advise" },
  ],
};

const de_copy: typeof en_copy = {
  eyebrow: "Behandlung verfügbar",
  h1: "Persönliche Beratung anfragen",
  lead:
    "Wir bieten den betreuten Zugang zu Retatrutid, Wegovy und Mounjaro über ein spezialisiertes Ärzteteam. Schildere uns kurz deine Situation – wir melden uns innerhalb eines Werktags.",
  formTitle: "Beratungsanfrage",
  formSub: "Kostenloses Erstgespräch. Unverbindlich.",
  fName: "Vollständiger Name",
  fEmail: "E-Mail",
  fPhone: "Telefon (optional)",
  fTreatment: "Gewünschte Therapie",
  fMethod: "Bevorzugter Kontaktweg",
  fMessage: "Deine Nachricht",
  messagePlaceholder:
    "Was sollten wir vorab wissen? Zielgewicht, Vorerkrankungen, Zeitplan …",
  methodEmail: "E-Mail",
  methodPhone: "Telefon / WhatsApp",
  consent:
    "Ich habe den Datenschutzhinweis gelesen und stimme zu, dass meine Daten zur Beantwortung dieser Anfrage genutzt werden.",
  submit: "Beratungsanfrage senden",
  disclaimer:
    "Dieses Formular begründet kein Behandlungsverhältnis. Verordnung und Therapieentscheidungen gehören in ärztliche Hand.",
  thanksTitle: "Danke – deine Anfrage ist eingegangen.",
  thanksBody:
    "Unser Ärzteteam meldet sich innerhalb eines Werktags bei dir. Eine Zusammenfassung deiner Anfrage findest du unten für deine Unterlagen.",
  thanksAgain: "Weitere Anfrage senden",
  infoTitle: "Direkter Kontakt",
  infoMail: "Per E-Mail",
  infoTeam: "Ärztliches Team",
  infoTeamBody:
    "Die Beratung erfolgt durch Ärztinnen und Ärzte mit Erfahrung in Adipositas-Medizin und GLP-1-Therapie.",
  infoPrivacy: "Daten & Datenschutz",
  infoPrivacyBody:
    "Deine Daten werden ausschließlich zur Beantwortung dieser Anfrage genutzt. Kein Newsletter, keine Weitergabe.",
  fyiTitle: "Bevor du schreibst",
  fyiBody:
    "Retatrutid ist über das genehmigte Phase-III-Studienprogramm unserer Partner-Klinik für geeignete Teilnehmer zugänglich. Wegovy und Mounjaro sind zugelassen und können heute verordnet werden.",
  treatmentOptions: [
    { value: "retatrutide", label: "Retatrutid (klinische Studie)" },
    { value: "wegovy", label: "Wegovy (Semaglutid)" },
    { value: "mounjaro", label: "Mounjaro (Tirzepatid)" },
    { value: "unsure", label: "Noch unsicher – bitte beraten" },
  ],
};
